import React, { useState, useEffect } from "react";
import { Wifi, Mic, Volume2, Video } from "lucide-react";
import AudioVisualizer from "./AudioVisualizer";
import SpeakerVisualizer from "./SpeakerVisualizer";
import VideoComponent from './videoVisualizer'
import logger from './utils/logger.js';

const Tab = {
  id: '',
  label: '',
  icon: React.Component,
};


const tabs = [
  { id: 'network', label: 'Network', icon: Wifi },
  { id: 'audio', label: 'Audio', icon: Mic },
  { id: 'speakers', label: 'Speakers', icon: Volume2 },
  { id: 'video', label: 'Video', icon: Video },
];

const HelpPopup = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('network');
  const [ping, setPing] = useState(0);
  const [audioInputDevices, setAudioInputDevices] = useState([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState([]);
  const [selectedInputDevice, setSelectedInputDevice] = useState('');
  const [selectedOutputDevice, setSelectedOutputDevice] = useState('');
  const [audioQuality, setAudioQuality] = useState('');
  const [cameraDevices, setCameraDevices] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState('');
  const [videoQuality, setVideoQuality] = useState(null);
  const [width, setWidth] = useState(window.innerWidth);

  const getCameraDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices.filter(device => device.kind === 'videoinput');
      setCameraDevices(cameras);
      if (cameras.length > 0) {
        setSelectedCamera(cameras[0].deviceId);
      }
    } catch (error) {
      logger.error('Error getting camera devices:', error);
    }
  };
  
    const handleNext = () => {
      const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
      if (currentIndex < tabs.length - 1) {
        setActiveTab(tabs[currentIndex + 1].id); // Move to the next tab
      }
    };
  
    const handleYes = () => {
      if (activeTab === 'video') {
        closePopup(); // Close the popup if on video tab
      } else {
        const currentIndex = tabs.findIndex(tab => tab.id === activeTab);
        if (currentIndex < tabs.length - 1) {
          setActiveTab(tabs[currentIndex + 1].id); // Move to the next tab
        }
      }
    };
  

  const closePopup = () => {
    setIsOpen(false);
    setActiveTab('network');
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('helpPopupClosed');
      window.dispatchEvent(event);
    }
  };
  

  const fetchPing = () => {
    const simulatedPing = Math.floor(Math.random() * 100);
    setPing(simulatedPing);
  };

  useEffect(() => {
    if (activeTab === 'video') {
      getCameraDevices();
    }
  }, [activeTab]);

  useEffect(() => {
    const interval = setInterval(fetchPing, 1000);
    return () => clearInterval(interval);
  }, []);

  const getAudioDevices = async () => {
    try {
      // First request audio permissions
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Add a default system audio output if none are found
      const defaultOutput = {
        deviceId: 'default',
        kind: 'audiooutput',
        label: 'System Default Speaker',
        groupId: ''
      };

      // Enumerate devices after permissions are granted
      const devices = await navigator.mediaDevices.enumerateDevices();
      logger.debug('All devices:', devices);
      
      const inputDevices = devices.filter(device => device.kind === 'audioinput');
      const outputDevices = devices.filter(device => device.kind === 'audiooutput');
      
      // If no output devices are found, add the default system output
      if (outputDevices.length === 0) {
        outputDevices.push(defaultOutput);
      }
      
      logger.debug('Input Devices:', inputDevices);
      logger.debug('Output Devices:', outputDevices);

      setAudioInputDevices(inputDevices);
      setAudioOutputDevices(outputDevices);
      
      // Set default selections
      if (inputDevices.length > 0) {
        setSelectedInputDevice(inputDevices[0].deviceId);
      }
      setSelectedOutputDevice('default'); // Always set the default output device

      // Clean up the audio stream
      stream.getTracks().forEach(track => track.stop());

    } catch (error) {
      logger.error("Error fetching audio devices:", error);
      // Add a default output device even if there's an error
      setAudioOutputDevices([{
        deviceId: 'default',
        kind: 'audiooutput',
        label: 'System Default Speaker',
        groupId: ''
      }]);
      setSelectedOutputDevice('default');
      
      if (error.name === 'NotAllowedError') {
        logger.debug('Permission to access audio devices was denied');
      } else if (error.name === 'NotFoundError') {
        logger.debug('No media devices found. Please check your connections.');
      } else {
        logger.debug('An unexpected error occurred:', error);
      }
    }
  };

  useEffect(() => {
    getAudioDevices();
  }, []);

  const renderContent = () => {
    switch (activeTab) {
      case "network":
        return (
          <div className="p-4">
            <div className="text-left mt-2 ml-4 mb-3" style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '16px',
              fontWeight: '500',
              lineHeight: '24px',
              letterSpacing: '-0.3px',
              textAlign: 'left',
              textDecoration: 'none',
            }}>
              Network
            </div>
            <div className="bg-white" style={{
              width: width < 768 ? '95%' : '518px',
              height: width < 768 ? '240px' : '280px',
              gap: '0px',
              borderRadius: '8px',
              border: '1px solid var(--Gray-200, #DADADD)',
              padding: width < 768 ? '12px' : '16px',
              position: 'relative'
            }}>
              <h3 className="font-bold text-lg mb-6" style={{ fontFamily: 'Inter, sans-serif' }}>Network stability</h3>
              <h4 className="font-semibold text-md mb-5" style={{ fontFamily: 'Inter, sans-serif',color: '#93949d'}}>Recommendations</h4>
              <ul className="space-y-2 text-sm mt-4">
                <li className="flex items-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <Wifi className="mr-2 w-4 h-4" />
                  If you're using Wi-Fi, try moving closer to your router
                </li>
                <li className="flex items-center" style={{ fontFamily: 'Inter, sans-serif' }}>
                  <img 
                    src="https://img.icons8.com/ios-filled/50/000000/joystick.png" 
                    alt="Joystick icon" 
                    className="mr-2 w-4 h-4" 
                  />
                  Avoid activities that heavily use your internet connection
                </li>
              </ul>
              <p className="text-md font-semibold mt-4 mb-2" style={{ fontFamily: 'Inter, sans-serif', color:'#93949d' }}>
                Connection Delay
              </p>
              <p className="text-sm text-500" style={{ fontFamily: 'Inter, sans-serif' }}>
                High values or large variations may reduce call quality.
              </p>
              <div className="text-sm text-gray-500" style={{ 
                fontFamily: 'Inter, sans-serif', 
                position: 'absolute',
                bottom: '16px',
                right: '16px'
              }}>
                Ping: {ping} ms {/* Displaying the ping value */}
              </div>
            </div>
            
          </div>
        );
      case "audio":
        return (
          <div className="p-4" style={{ fontFamily: 'Inter, sans-serif' }}>
            <div className="flex flex-col gap-2" style={{ width: width < 768 ? '100%' : '518px' }}>
              <div className="flex items-center" style={{
                fontFamily: 'Inter, sans-serif',
                fontSize: width < 768 ? '14px' : '16px',
                fontWeight: '500',
                lineHeight: '20px',
                letterSpacing: '-0.3px',
                textAlign: 'left',
                textDecoration: 'none',
              }}>
                Microphone Audio
              </div>
              <select 
                className="w-full h-6 p-1 rounded-md border border-gray-300"
                value={selectedInputDevice}
                onChange={(e) => setSelectedInputDevice(e.target.value)} 
              >
                <option value="">Select Microphone</option>
                {audioInputDevices.map(device => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Microphone ${audioInputDevices.indexOf(device) + 1}`}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginTop: width < 768 ? '25px' : '20px' }} />
            <div className="w-full h-6 p-1 rounded-md" style={{ 
              display: 'flex', 
              padding: '0px 2px', 
              gap: '4px',
              maxWidth: width < 768 ? '100%' : 'none' 
            }}>
              <AudioVisualizer setAudioQuality={setAudioQuality} />
            </div>
            <p className="w-[100%] h-[80px] text-left mt-2 text-[20px] font-medium leading-[24px] tracking-[-0.5px] text-left decoration-none" style={{ marginTop: width < 768 ? '25px' : '20px' }}>
  Press the button and speak into your microphone.
</p>
<p className="w-[100%] text-left mt-[-8px] text-[16px] font-normal leading-[20px] tracking-[-0.4px] text-[#93949d]">
  Let us know if you were able to speak clearly and if everything worked as expected.
</p>



            {audioQuality && (
              <div className="mt-2 text-gray-500">
                Audio Quality: {audioQuality}
              </div>
            )}
          </div>
        );
        case "speakers":
  return (
    <div className="p-3" style={{ 
      fontFamily: 'Inter, sans-serif',
      width: width < 768 ? '100%' : 'auto'
    }}>
      {/* Speaker Output Title */}
      <div className="text-left mt-1 ml-4 mb-2" style={{ /* Reduced margins */
        fontFamily: 'Inter, sans-serif',
        fontSize: '16px',
        fontWeight: '500',
        lineHeight: '24px',
        letterSpacing: '-0.3px',
        textAlign: 'left',
        textDecoration: 'none',
      }}>
        Speaker 
      </div>

      {/* Dropdown to Select Output Device */}
      <div className="flex flex-col gap-2 mb-4" style={{ 
        width: width < 768 ? '100%' : '518px'
      }}>
        <label
          htmlFor="output-device-select"
          className="text-sm font-semibold"
          style={{ fontFamily: 'Inter, sans-serif', color: '#93949d' }}
        >
          Select Output Device
        </label>
        <select
          id="output-device-select"
          className="w-full h-8 p-2 rounded-md border border-gray-300"
          value={selectedOutputDevice}
          onChange={(e) => setSelectedOutputDevice(e.target.value)}
        >
          <option value="">Select Speaker Device</option>
          {audioOutputDevices.map(device => (
            <option key={device.deviceId} value={device.deviceId}>
              {device.label || `Speaker ${audioOutputDevices.indexOf(device) + 1}`}
            </option>
          ))}
        </select>
      </div>

      {/* "Can you hear this sound clearly?" Text */}
      <div
        style={{
          width: '518px',
          height: '30px',
          gap: '0px',
          fontFamily: 'Inter, sans-serif',
          fontSize: '20px',
          fontWeight: '500',
          lineHeight: '30px',
          letterSpacing: '-0.5px',
          textAlign: 'left',
          textUnderlinePosition: 'from-font',
          textDecorationSkipInk: 'none',
          
          color: 'black', // Add white color to make text visible
          padding: '4px 8px',
          borderRadius: '6px', // Optional: Add slight rounding for a polished look
        }}
        className="rounded-md"
      >
        Can you hear this sound clearly?
      </div>

      {/* Sound Bar */}
      <div className="w-full h-8 p-2 rounded-md mt-4" style={{ display: 'flex', padding: '0px 2px', gap: '8px' }}>
        <SpeakerVisualizer setAudioQuality={setAudioQuality} />
      </div>
    </div>
  );


        
  case "video":
    return (
      <div style={{ 
        padding: width < 768 ? '12px' : '0',
        width: width < 768 ? '100%' : 'auto'
      }}>
        <div style={{ 
          position: 'relative', 
          width: width < 768 ? '100%' : '500px',
          marginLeft: width < 768 ? '0' : '30px'
        }}>
          <select
            className="w-full h-8 p-2 rounded-md border border-gray-300"
            style={{ 
              padding: '6px 10px', 
              borderRadius: '6px',
              marginBottom: '15px'
            }}
            value={selectedCamera}
            onChange={(e) => setSelectedCamera(e.target.value)}
          >
            <option value="">Select Camera Device</option>
            {cameraDevices.length > 0 ? (
              cameraDevices.map(device => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Camera ${cameraDevices.indexOf(device) + 1}`}
                </option>
              ))
            ) : (
              <option disabled>No camera devices found</option>
            )}
          </select>
        </div>

        <div style={{
          width: width < 768 ? '100%' : '500px',
          height: width < 768 ? '180px' : '250px', // Reduced height for mobile
          gap: '2px',
          borderRadius: '10px',
          overflow: 'hidden',
          marginLeft: width < 768 ? '0' : '30px',
        }}>
          <VideoComponent setVideoQuality={setVideoQuality} selectedDevice={selectedCamera} />
        </div>

        {/* Video Quality */}
        {videoQuality && (
          <div className="mt-2 text-gray-500">
            Video Quality: {videoQuality}
          </div>
        )}
      </div>
    );
      default:

        return null;
    }
  };

  return (
    <div style={{ fontFamily: 'Inter, sans-serif' }}>
      {isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-50">
<div
  className="bg-white w-96 rounded-[12px] shadow-lg absolute"
  style={{
    width: width < 768 ? '96%' : '560px',
    height: width < 768 ? '86%' : '525px',
    top: width < 768 ? '50%' : '64px',
    left: width < 768 ? '50%' : 'auto',
    transform: width < 768 ? 'translate(-50%, -50%)' : 'none',
    gap: '0px',
    background: 'var(--base-White, #FFFFFF)',
    boxShadow: '0px 25px 50px -12px #00000040, 0px 0px 0px 1px #1118271A',
    maxHeight: width < 768 ? '600px' : 'auto',
    overflowY: width < 768 ? 'auto' : 'visible',
  }}
>
            {/* Help Icon and Header Container */}
            <div className="flex items-center" style={{
              width: width < 768 ? '100%' : '550px',
              height: '48px',
              padding: '8px 16px',
              gap: '0px',
              borderBottom: '1px solid #ccc',
              justifyContent: 'space-between',
              background: 'white',
              borderRadius: '12px 12px 0 0',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <div className="flex items-center" style={{ gap: '10px' }}>
                <img 
                  src="https://img.icons8.com/?size=100&id=8GKNsu0eFMQw&format=png&color=000000" 
                  alt="help icon"
                  className="w-5 h-5 object-contain"
                />
                <div className="flex items-center" style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '16px',
                  fontWeight: '500',
                  lineHeight: '24px',
                  letterSpacing: '-0.3px',
                  textAlign: 'left',
                  color: 'black',
                  width: '112px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  Help & Support
                </div>
              </div>
              {/* Close Buttons */}
              <div className="flex items-center" style={{
                width: '53px',
                height: '48px',
                gap: '8px',
              }}>
                <button className="text-gray-500 hover:text-gray-700" style={{
                  width: '29px',
                  height: '23px',
                  padding: '0px var(--spacing-4)',
                  borderRadius: '4px',
                  border: '1px solid var(--base-White, #FFFFFF)',
                  background: 'var(--gray-100, #F3F4F6)',
                  boxShadow: '0px 1px 2px 0px #0000000F, 0px 1px 3px 0px #0000001A, 0px 0px 0px 1px #1118271A',
                  cursor: 'pointer'
                }}>
                  Esc
                </button>
                <button type="button" className="bg-white rounded-md p-2 inline-flex items-center justify-center text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500" onClick={closePopup}>
                  <span className="sr-only">Close menu</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="border-b border-gray-300 mb-2"></div>

            {/* Navigation Tabs */}
            <div className="p-1 bg-gray-100 rounded-full inline-flex gap-1" style={{
              width: '550px',
              height: '36px',
              borderRadius: '99px',
            }}>
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center gap-2 px-6 py-3 rounded-full text-sm transition-colors
                      ${activeTab === tab.id ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}
                    `}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-lg">{tab.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="border-b border-gray-300 mt-2"></div>

            {/* Content */}
            {renderContent()} 

            {/* Actions */}
            <div
  style={{
    display: 'flex',
    justifyContent: 'space-between', // Distribute buttons with equal space between them
    position: 'absolute',
    bottom: '20px',
    width: '100%',
    padding: '0 20px', // Add equal padding on both left and right
  }}
>
<button
  className="bg-blue-500 text-black"
  style={{
    width: '254px',
    height: '40px',
    padding: '2px 10px',
    gap: '6px',
    borderRadius: '8px',
    background: 'var(--White, #FFFFFF)',
    border: '1px solid var(--gray, #FFFFFF)',
    boxShadow:
      '0px 0px 0px 1px #0000001A,0px 0.5px 1px 0px #0000001F,0px 2px 6px 0px #00000014',
  }}
>
  {(activeTab === 'audio' || activeTab === 'speakers' || activeTab === 'video') ? 'No, try Again?' : 'Run Diagnostics'}
</button>
<button
  className="bg-blue-500 text-white"
  style={{
    width: '254px',
    height: '40px',
    padding: '2px 10px',
    gap: '10px',
    borderRadius: '8px',
    border: '1px solid transparent',
    background: `linear-gradient(0deg, var(--Black, #000000), var(--Black, #000000)),
                linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, rgba(255, 255, 255, 0) 100%)`,
    boxShadow: '0px 6px 13px 0px #00000026',
  }}
  onClick={activeTab === 'audio' || activeTab === 'speakers' ? handleYes : handleNext}
>
  {(activeTab === 'audio' || activeTab === 'speakers' || activeTab === 'video') ? 'Yes' : 'Next'}
</button>

</div>

          </div>
        </div>
      )}
    </div>
  );
};

export default HelpPopup;
